import * as Yup from "yup";
import { Mutation, MutationGenerateGenericPresignedUrlArgs } from "../../../generated/nest-graphql";
import { Form, Formik } from "formik";

import Typography from "@material-ui/core/Typography";
import React, { useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import { useMutation } from "@apollo/client";
import { GENERATE_GENERIC_PRESIGNED_URL } from "../../../graphql/mutations/generateGenericPresignedUrl";
import { useSimpleS3Upload } from "../../../hooks/useS3Upload";
import { TextField } from "../../Forms/fields/TextField";
import { SubmitButton } from "../../Buttons/SubmitButton";
import Webcam from "react-webcam";
import { Button, ButtonGroup } from "@material-ui/core";
import { useDispatch } from "../../../contexts/snackbar-context";
import { showErrorAlert, showSuccessAlert } from "../../../actions";
import { Camera, FlipCameraIos, Cancel } from "@material-ui/icons";

export type FileFormValues = {
  caption: string;
  fileName: string;
  url: string;
  files: any[];
};

const fileFormValidationSchema = Yup.object().shape({
  caption: Yup.string().notRequired(),
  url: Yup.string().required("Required"),
  fileName: Yup.string().required("Required"),
});
export const PhotoForm: React.FC<{
  contactId: string;
  jobId: string;
  name: string;
  fieldName: string;
  optionsLabel?: string[];
  onSubmit: (values, helper) => void;
  onClose: () => void;
  initialValues: FileFormValues;
}> = ({ contactId, jobId, initialValues, onSubmit, onClose, name, fieldName, optionsLabel }) => {
  return (
    <Formik<FileFormValues>
      initialValues={initialValues}
      onSubmit={(values, helper) => {
        onSubmit(values, helper);
        onClose();
      }}
      validationSchema={fileFormValidationSchema}
      validateOnMount={true}
    >
      {({ isValid, isSubmitting, values, setFieldValue, setValues, resetForm }) => {
        return (
          <FileFormInternals
            contactId={contactId}
            jobId={jobId}
            name={name}
            fieldName={fieldName}
            values={values}
            optionsLabel={optionsLabel}
            setFieldValue={setFieldValue}
            setValues={setValues}
            isSubmitting={isSubmitting}
            isValid={isValid}
            resetForm={resetForm}
          />
        );
      }}
    </Formik>
  );
};

/**
 * Turns a getUserMedia failure into something a technician can act on. Without
 * this the preview area simply renders empty and there is no clue why.
 */
const describeCameraError = (error: string | DOMException): string => {
  const name = typeof error === "string" ? error : error.name;

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Camera access was blocked. Allow camera permission for this site and try again.";
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return "No usable camera was found on this device.";
    case "NotReadableError":
    case "TrackStartError":
      return "The camera is already in use by another app.";
    default:
      return "The camera could not be started. Note that it only works over HTTPS or on localhost.";
  }
};

const WebcamCapture = ({
  onCapture,
  fileName,
  isValid,
  children,
}: {
  fileName: string;
  isValid: boolean;
  onCapture?: (file: File) => void;
  children?: any;
}) => {
  // Prefer the rear camera: that's the one pointed at the vehicle. Desktops
  // don't have one, which is handled by the constraint below rather than here.
  const [useRearCamera, setUseRearCamera] = useState(true);
  const [image, setImage] = useState<string>();
  const [cameraError, setCameraError] = useState<string | null>(null);
  const dispatch = useDispatch();
  return (
    <>
      {!!image && (
        <div>
          <img src={`${image}`} alt="Captured" />
          {children}
          <Button
            size={"large"}
            type={"button"}
            variant={"contained"}
            fullWidth={true}
            className="mt-8"
            color={"secondary"}
            startIcon={<Cancel />}
            disabled={!isValid}
            onClick={() => {
              setImage("");
            }}
          >
            Retake Photo
          </Button>
        </div>
      )}
      {!image && (
        <>
          {!!cameraError && (
            <Typography variant="body2" style={{ color: "#f04e23", marginTop: "1rem" }}>
              {cameraError}
            </Typography>
          )}
          <Webcam
            audio={false}
            height={1080} //1280
            screenshotFormat="image/jpeg"
            width={720}
            onUserMedia={() => setCameraError(null)}
            onUserMediaError={(error) => setCameraError(describeCameraError(error))}
            videoConstraints={{
              /*
               * These are all "ideal" constraints, never `exact`.
               *
               * `facingMode: { exact: "environment" }` asks for a rear
               * camera and refuses anything else, so on a laptop —
               * which only has a user-facing webcam — getUserMedia
               * rejects with OverconstrainedError and the preview
               * never renders at all. As a plain (ideal) value, phones
               * still get the rear camera and a desktop falls back to
               * whatever single webcam it has.
               */
              width: { ideal: 720 },
              height: { ideal: 1080 },
              facingMode: useRearCamera ? "environment" : "user",
            }}
          >
            {({ getScreenshot }) => (
              <div className="flex flex-col gap-4">
                <Button
                  size={"large"}
                  type={"button"}
                  variant={"contained"}
                  fullWidth={true}
                  className="mt-8"
                  color={"primary"}
                  startIcon={<Camera />}
                  onClick={() => {
                    const imageSrc = getScreenshot();
                    setImage(imageSrc);
                    if (onCapture) {
                      onCapture(base64ToFile(imageSrc, `${fileName}.jpeg`));
                      showSuccessAlert(dispatch, "Photo taken!");
                    }
                  }}
                >
                  Capture photo
                </Button>
                <Button
                  size={"large"}
                  type={"button"}
                  variant={"contained"}
                  fullWidth={true}
                  className="mt-8"
                  color={"primary"}
                  startIcon={<FlipCameraIos />}
                  onClick={() => {
                    setUseRearCamera(!useRearCamera);
                  }}
                >
                  Toggle camera
                </Button>
              </div>
            )}
          </Webcam>
        </>
      )}
    </>
  );
};

// Convert base64 to File
const base64ToFile = (base64String: string, filename: string): File => {
  const [header, base64Data] = base64String.split(",");
  const mimeType = header.match(/:(.*?);/)?.[1] ?? ""; // Extract mime type from header
  const binaryData = atob(base64Data); // Decode base64 string

  // Convert binary data to an array of bytes
  const byteArray = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    byteArray[i] = binaryData.charCodeAt(i);
  }

  // Create a Blob from the byte array and mime type
  const blob = new Blob([byteArray], { type: mimeType });

  // Convert the Blob to a File object
  return new File([blob], filename, { type: mimeType });
};

const FileFormInternals: React.FC<{
  resetForm: any;
  values: FileFormValues;
  setFieldValue: any;
  setValues: any;
  contactId: string;
  jobId: string;
  name: string;
  fieldName: string;
  optionsLabel?: string[];
  isSubmitting: boolean;
  isValid: boolean;
}> = ({
  isSubmitting,
  isValid,
  values,
  setFieldValue,
  setValues,
  contactId,
  jobId,
  name,
  fieldName,
  optionsLabel,
  resetForm,
}) => {
  const dispatch = useDispatch();
  const [generatePresignedUrl] = useMutation<Mutation, MutationGenerateGenericPresignedUrlArgs>(
    GENERATE_GENERIC_PRESIGNED_URL
  );
  const { files: myFiles } = values;
  const setMyFiles = (acceptedFiles) => setFieldValue("files", acceptedFiles);
  const { onUpload } = useSimpleS3Upload({
    myFiles,
    setMyFiles,
    contactId,
    onError: (error?: any) => {
      // Previously console.error only, which left the technician staring at a
      // permanently disabled "Confirm & Upload" with no explanation.
      const detail = error?.message ? ` (${error.message})` : "";
      showErrorAlert(dispatch, `The photo could not be uploaded${detail}. Please try again.`);
    },
    onUploadDone: async ({ url, fileName }) => {
      /*
       * Both fields in ONE update.
       *
       * Formik validates against `setIn(state.values, field, value)` — the
       * values as of the current render, plus the single field being set. Two
       * setFieldValue calls in the same tick therefore each validate a snapshot
       * missing the other's change: one reports `fileName: Required`, the other
       * `url: Required`, and whichever resolves last leaves a stale error
       * behind. isValid never became true and the button stayed disabled
       * forever, even though both values were correctly stored.
       */
      setValues((prev) => ({ ...prev, url, fileName }));
    },
    onUploadStart: async (img) => {
      /*console.log("img", img)
                resetForm({
                    caption: "",
                    url: "",
                    fileName: "",
                });*/
      const result = await generatePresignedUrl({
        variables: {
          path: `contacts/${contactId}/${img}`,
        },
      });
      return result.data.generateGenericPresignedUrl;
    },
  });

  /*const files = values.files.map((file: any) => (
            <li key={file.path}>
                {file.path} - {file.size} bytes
            </li>
        ));*/

  // Default to a placement that is actually still available, so the first photo
  // for a part isn't named after a slot that's already taken.
  const [selectedPhotoPlace, setsSelectedPhotoPlace] = useState(optionsLabel?.[0] ?? "front");

  /*
   * Derived rather than stored. This used to be state initialised to "" and
   * assigned only inside onFileNameChange, so a technician who accepted the
   * pre-selected placement and went straight to Capture uploaded a file named
   * ".jpeg" — which also never matched back to its field, since FormGenerator
   * parses photos as `jobId-placement-field`.
   */
  const finalFileName = `${jobId}-${selectedPhotoPlace}-${fieldName}`;

  const applyPlacement = React.useCallback(
    (place: string) => {
      setsSelectedPhotoPlace(place);
      // One update, for the same reason as onUploadDone above.
      setValues((prev) => ({
        ...prev,
        caption: `${jobId}-${place}-${fieldName}`,
        fileCategory: `${place}-${fieldName}`,
      }));
    },
    [jobId, fieldName, setValues]
  );

  useEffect(() => {
    applyPlacement(selectedPhotoPlace);
    // Seed caption/fileCategory from the default placement on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFileNameChange = (place: string) => applyPlacement(place);

  if (optionsLabel.length === 0) {
    return <Typography color="error">Maximum number of photos reached for this part</Typography>;
  }
  return (
    <Form>
      <Box>
        <Typography>Take a {name} photo</Typography>
        <Box>
          <div className="flex flex-row justify-center my-8">
            <div className="flex flex-wrap gap-2">
              {optionsLabel.includes("front") && (
                <button
                  type="button"
                  onClick={() => onFileNameChange("front")}
                  className={`px-6 py-2 rounded-full border transition-colors ${
                    selectedPhotoPlace === "front" ? "bg-opacity-50 shadow-2xl text-white" : "text-white"
                  } bg-gray-400 hover:bg-gray-600 flex items-center`}
                  style={{
                    boxShadow: selectedPhotoPlace === "front" ? "0 4px 6px rgba(0, 0, 0, 0.1)" : "none",
                  }}
                >
                  Front
                </button>
              )}
              {optionsLabel.includes("rear") && (
                <button
                  type="button"
                  onClick={() => onFileNameChange("rear")}
                  className={`px-6 py-2 rounded-full border transition-colors ${
                    selectedPhotoPlace === "rear" ? "bg-opacity-50 shadow-2xl text-white" : "text-white"
                  } bg-gray-400 hover:bg-gray-600 flex items-center`}
                  style={{
                    boxShadow: selectedPhotoPlace === "rear" ? "0 4px 6px rgba(0, 0, 0, 0.1)" : "none",
                  }}
                >
                  Rear
                </button>
              )}

              {optionsLabel.includes("side") && (
                <button
                  type="button"
                  onClick={() => onFileNameChange("side")}
                  className={`px-6 py-2 rounded-full border transition-colors ${
                    selectedPhotoPlace === "side" ? "bg-opacity-50 shadow-2xl text-white" : "text-white"
                  } bg-gray-400 hover:bg-gray-600 flex items-center`}
                  style={{
                    boxShadow: selectedPhotoPlace === "side" ? "0 4px 6px rgba(0, 0, 0, 0.1)" : "none",
                  }}
                >
                  Side
                </button>
              )}
              {optionsLabel.includes("side") && (
                <button
                  type="button"
                  onClick={() => onFileNameChange("other")}
                  className={`px-6 py-2 rounded-full border transition-colors ${
                    selectedPhotoPlace === "other" ? "bg-opacity-50 shadow-2xl text-white" : "text-white"
                  } bg-gray-400 hover:bg-gray-600 flex items-center`}
                  style={{
                    boxShadow: selectedPhotoPlace === "other" ? "0 4px 6px rgba(0, 0, 0, 0.1)" : "none",
                  }}
                >
                  Other
                </button>
              )}
            </div>
          </div>
        </Box>
        <Box className="py-4">
          <WebcamCapture
            fileName={finalFileName}
            isValid={isValid}
            onCapture={(file: File) => {
              // Awaited via .catch rather than fire-and-forget: an upload that
              // rejects (CORS, offline, DNS) used to become an unhandled promise
              // rejection and the technician saw nothing at all.
              Promise.resolve(onUpload([file])).catch(() => {
                /* reported by the hook's onError */
              });
            }}
          >
            <SubmitButton label={"Confirm & Upload"} isSubmitting={isSubmitting} isValid={isValid} />
          </WebcamCapture>
        </Box>
        {/*} <Box
                        {...getRootProps({ className: "dropzone bg-black-900 p-4 rounded" })}
                    >
                        <input {...getInputProps()} />
                        <Typography variant="body1">Tap here to upload a file</Typography>
                    </Box>*/}
        <Box className="py-4">
          <TextField name={"caption"} label={"Caption"} disabled />
        </Box>
        <br />
        {/*<Box>
                        <h4>Files</h4>
                        <ul>{files}</ul>
                    </Box>*/}
      </Box>
      {/*<div className="flex flex-row justify-between">
                    <div>
                    <SubmitButton
                            label={"Upload"}
                            isSubmitting={isSubmitting}
                            isValid={isValid}
                        />
                    </div>
                </div>*/}
    </Form>
  );
};
